import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { AskHrChatFeatureId } from '@core/ask-hr-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { HeaderThreadComponent, HrAssistantChatFeatureService } from '@core/intelligence-smart-ui';
import { HeadingComponent } from '@core/user-interface-legacy';
import {
  canActivateDataSource,
  SimpleViewComponent,
  ViewsLocalStorageService,
} from '@core/views-legacy';
import { WrappedTableListGroupHeadingComponent } from '@core/views-ui';
import {
  dataSourceProviders,
  provideViewDataScope,
  provideListLayout,
  provideViewBaseRoute,
} from '@core/views-util';

import { ChatUserTemplatesDataSourceService } from './chat-user-templates-data-source.service';
import { UserFormListItemComponent } from './chat-user-templates-list-item/chat-user-form-list-item.component';
import { ChatRequestFormComponent } from '../chat-request-form/chat-request-form.component';

@Component({
  selector: 'tlc-chat-user-forms-view',
  template: `
    <tlc-chat-header-thread
      (back)="templateId() ? clearSelectedTemplate() : backToWelcome()"
      (close)="onClose()"
    />
    @if (templateId(); as id) {
      <ask-chat-request-form
        [templateId]="id"
        (cancelClick)="clearSelectedTemplate()"
        (submitted)="onSubmitted()"
      />
    } @else {
      <div margin-lr-medium margin-top>
        <rt-heading variant="heading large">
          {{ 'tlc.ask_hr.forms.chat.select_type' | translate }}
        </rt-heading>
      </div>
      @if (canActivate()) {
        <rt-simple-view
          class="view"
          viewId="ask_templates:hr_assistant"
          [selfScrollEnabled]="true"
          [autoSetHeight]="false"
          [hasControls]="false"
        />
      }
    }
  `,
  imports: [
    SimpleViewComponent,
    HeadingComponent,
    HeaderThreadComponent,
    TranslatePipe,
    ChatRequestFormComponent,
  ],
  providers: [
    ViewsLocalStorageService,
    provideViewBaseRoute('/asks/templates'),
    ChatUserTemplatesDataSourceService,
    dataSourceProviders('ask_templates', {
      ask_templates: ChatUserTemplatesDataSourceService,
    }),
    provideViewDataScope({ useValue: 'ask-templates-chat' }),
    provideListLayout({
      ask_templates: {
        header: WrappedTableListGroupHeadingComponent,
        record: UserFormListItemComponent,
        recordSettings: {
          hasPadding: false,
        },
      },
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./chat-templates-list-feature.component.less'],
})
export class ChatTemplatesListFeatureComponent {
  private readonly hrAssistantFeaturesService = inject(HrAssistantChatFeatureService);
  private readonly canActivateFn = canActivateDataSource();
  private readonly canActivate$ = this.canActivateFn();
  private readonly dataSourceService = inject(ChatUserTemplatesDataSourceService);

  protected canActivate = toSignal(this.canActivate$);
  protected templateId = computed(() => this.dataSourceService.selectedTemplateId());

  protected clearSelectedTemplate(): void {
    this.dataSourceService.selectedTemplateId.set(null);
  }

  protected onClose() {
    this.hrAssistantFeaturesService.close$.next();
  }

  protected backToWelcome() {
    this.hrAssistantFeaturesService.clearFeature();
  }

  protected onSubmitted() {
    this.hrAssistantFeaturesService.setFeatureById(AskHrChatFeatureId.Tickets);
  }
}
